import React, { Fragment, useState, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { Menu, Popover, Transition } from "@headlessui/react";
import { SearchIcon, SpeakerphoneIcon } from "@heroicons/react/solid";
import {
    FireIcon,
    MenuIcon,
    CogIcon,
    DocumentTextIcon,
    XIcon,
    NewspaperIcon,
    FolderOpenIcon,
    UserIcon,
    TagIcon,
    LogoutIcon,
    CubeTransparentIcon,
    SearchIcon as SearchIconOutline,
    SupportIcon,
    CalendarIcon,
    ThumbUpIcon,
    BellIcon,
    ChatAltIcon,
    ExternalLinkIcon,
    ChartBarIcon,
} from "@heroicons/react/outline";
import flatCube from "../../assets/flat-cube.svg";
import { arrayRandom, packClasses } from "Utils";
import quips from "assets/quips.json";
import { connect, useSelector } from "react-redux";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { AppContext } from "context/App";
import { take } from "lodash";
import { useAuth } from "hooks/Auth";
import { useContextualHelp } from "hooks/Help";
import { buildProxyUrl } from "helpers/Request";
import UpdatePanel from "Panels/UpdatePanel";
import { getLocalStorageItem, setLocalStorageItem } from "helpers/LocalStorage";
import { useRegisterActions, Priority, useKBar } from "kbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import SegmentedMenu from "components/Menus/SegmentedMenu";
import UIUpperErrorBoundary from "ErrorHandlers/UIUpperErrorBoundary";
import ChaosMonkey from "Utils/ChaosMonkey";
import { modal } from "components/HeadlessModal";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { fromSqlDateTime } from "helpers/Date";

function getPlatformMeta() {
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)
        ? "\u2318"
        : "Ctrl";
}

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default connect((state) => {
    return { recentItems: state.recentItems };
})(function MainLayout(props) {
    const user = props.user ? props.user : {};
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();
    const [homeNav, setHomeNav] = useState("/app/dashboard");
    const [entryNav, setEntryNav] = useState("/app/custom-content");
    const [searchNav, setSearchNav] = useState("/app/search");
    const [plannerNav, setPlannerNav] = useState(
        "/app/planner?open=1&view=calendar"
    );
    const [currentNav, setCurrentNav] = useState("home");
    const [updatesOpen, setUpdatesOpen] = useState(false);
    const [lastNotice, setLastNotice] = useState(() => {
        return getLocalStorageItem("lastNotice20240110");
        // return true;
    });
    const { settings, panels } = useSelector((state) => ({
        settings: state.contentFlow?.settings,
        panels: state.contentFlow?.panels,
    }));
    const searchRef = useRef();
    const kbar = useKBar();

    useRegisterActions(
        [
            {
                id: "globalSearch",
                name: "Search",
                shortcut: ["/"],
                keywords: "search find",
                perform: () => {
                    setTimeout(() => {
                        searchRef.current.focus();
                    }, 250);
                },
                priority: Priority.LOW,
            },
            {
                id: "globalHelp",
                name: "Help",
                shortcut: ["?"],
                keywords: "help support how",
                perform: () => {
                    contextualHelp.showContextualHelp("/index.html");
                },
                priority: Priority.LOW,
            },
            {
                id: "globalChangePub",
                name: "Change Publication",
                perform: props.requestPublicationChange,
                priority: Priority.LOW,
            },
            {
                id: "globalNew",
                name: "New",
                keywords: "new create",
                priority: Priority.LOW,
            },
            {
                id: "newArticle",
                name: "New Article",
                parent: "globalNew",
                shortcut: ["c", "a"],
                perform: () => {
                    history.push("/app/article/new");
                },
            },
            {
                id: "newContainer",
                name: "New Container/Section",
                parent: "globalNew",
                shortcut: ["c", "c"],
                perform: () => {
                    history.push("/app/container/new");
                },
            },
            {
                id: "newPage",
                name: "New Page",
                parent: "globalNew",
                shortcut: ["c", "p"],
                perform: () => {
                    history.push("/app/page/new");
                },
            },
            {
                id: "newEntry",
                name: "New Entry",
                parent: "globalNew",
                shortcut: ["c", "e"],
                perform: () => {
                    history.push("/app/custom-content/entry/new");
                },
            },
            {
                id: "newGallery",
                name: "New Gallery",
                parent: "globalNew",
                shortcut: ["c", "g"],
                perform: () => {
                    history.push("/app/gallery/new");
                },
            },
            {
                id: "newAssignment",
                name: "New Assignment",
                parent: "globalNew",
                shortcut: ["c", "n"],
                perform: () => {
                    history.push("/app/planner/assignment/new");
                },
            },
            {
                id: "globalGo",
                name: "Go",
                keywords: "go to",
                priority: Priority.LOW,
            },
            {
                id: "goHome",
                parent: "globalGo",
                name: "Dashboard",
                shortcut: ["g", "h"],
                perform: () => {
                    history.push("/app/dashboard");
                },
            },
            {
                id: "goRecent",
                parent: "globalGo",
                name: "Recent",
                shortcut: ["g", "r"],
                perform: () => {
                    history.push("/app/recent");
                },
            },
            {
                id: "goContainers",
                parent: "globalGo",
                name: "Containers",
                shortcut: ["g", "c"],
                perform: () => {
                    history.push("/app/container");
                },
            },
            {
                id: "goCustomContent",
                parent: "globalGo",
                name: "Custom Content",
                shortcut: ["g", "e"],
                perform: () => {
                    history.push("/app/custom-content");
                },
            },
            {
                id: "goAnalytics",
                parent: "globalGo",
                name: "Analytics",
                perform: () => {
                    history.push("/app/analytics");
                },
            },
            {
                id: "goSearch",
                parent: "globalGo",
                name: "Search & DAM",
                shortcut: ["g", "s"],
                perform: () => {
                    history.push("/app/search");
                },
            },
        ],
        [history]
    );

    const ceo2Root = buildProxyUrl("/", auth.publication);
    const currentVer = process.env.REACT_APP_APP_VER;
    const lastUsedVer = getLocalStorageItem("lastVer");
    const showUpdateNotification =
        process.env.REACT_APP_SHOW_UPDATE_NOTIFICATION === "true" &&
        (!lastUsedVer || currentVer !== lastUsedVer);
    // const showUpdateNotification = true;
    const searchCacheHealth = settings?.settings?.search_cache_enabled
        ? true
        : false;
    let searchIndexHealth = settings?.settings?.last_search_cycle;
    if (settings?.settings?.last_search_cycle) {
        let check = fromSqlDateTime(
            settings?.settings?.last_search_cycle
        ).toMillis();
        check = Date.now() - check;
        check = check / 1000 / 60 / 60 / 24;
        if (check < 0.0) {
            searchIndexHealth = "Great";
        } else if (check < 1.0) {
            searchIndexHealth = "Good";
        } else if (check < 2.0) {
            searchIndexHealth = "Fair";
        } else if (check < 10) {
            searchIndexHealth = "Poor";
        } else {
            searchIndexHealth = "N/A";
        }
    }

    const navigation = [
        {
            name: "Content",
            to: homeNav,
            icon: DocumentTextIcon,
            isCurrent: (currentNav) =>
                currentNav === "content" || currentNav === "home",
        },
        {
            name: "Recent",
            to: "/app/recent",
            icon: FireIcon,
            isCurrent: (currentNav) => currentNav === "recent",
        },
        {
            name: "Containers",
            to: "/app/container",
            icon: FolderOpenIcon,
            isCurrent: (currentNav) => currentNav === "container",
        },
        {
            name: "Custom Content",
            to: entryNav,
            icon: CubeTransparentIcon,
            isCurrent: (currentNav) => currentNav === "custom-content",
        },
        {
            name: "Analytics",
            to: "/app/analytics",
            icon: ChartBarIcon,
            isCurrent: (currentNav) => currentNav === "analytics",
        },
        {
            name: "Search & DAM",
            to: searchNav,
            icon: SearchIconOutline,
            isCurrent: (currentNav) => currentNav === "search",
        },
        {
            name: "Planning",
            to: plannerNav,
            icon: CalendarIcon,
            isCurrent: (currentNav) => currentNav === "planner",
        },
        {
            name: "Tags",
            to: "/app/tag",
            icon: TagIcon,
            isCurrent: (currentNav) => currentNav === "tag",
        },
        {
            name: "Authors",
            to: "/app/author",
            icon: UserIcon,
            isCurrent: (currentNav) => currentNav === "author",
        },
        {
            name: "Alerts & Notifications",
            to: "/app/notification",
            icon: BellIcon,
            isCurrent: (currentNav) => currentNav === "notification",
            className: "hidden",
        },
        {
            name: "Audit",
            to: "/app/audit",
            icon: ThumbUpIcon,
            isCurrent: (currentNav) => currentNav === "audit",
            className: "hidden",
        },
        {
            name: "Settings",
            to: "/app/settings",
            icon: CogIcon,
            isCurrent: (currentNav) => currentNav === "settings",
        },
    ];

    const userNavigation = [
        (active) => (
            <button
                onClick={props.requestPublicationChange}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                Change Publication
            </button>
        ),

        (active) => {
            if (!user.can("admin")) {
                return <span></span>;
            }
            return (
                <a
                    href={process.env.REACT_APP_ACCOUNTS_DOMAIN}
                    className={classNames(
                        active ? "bg-gray-100" : "",
                        "block w-full py-2 px-4 text-left text-xs text-gray-700"
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    SNworks Accounts
                    <ExternalLinkIcon className="ml-1 inline h-3 w-3" />
                </a>
            );
        },
        (active) => (
            <a
                href={process.env.REACT_APP_ACCOUNTS_DOMAIN + "/app/profile"}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
                target="_blank"
                rel="noopener noreferrer"
            >
                Your Profile
                <ExternalLinkIcon className="ml-1 inline h-3 w-3" />
            </a>
        ),
        (active) => (
            <button
                onClick={() => kbar.query.toggle()}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                <div className="flex">
                    <div className="flex-grow">Command Menu</div>
                    <div className="flex-shrink text-right text-gray-500">
                        {getPlatformMeta()}+K
                    </div>
                </div>
            </button>
        ),
        (active) => (
            <button
                onClick={() => contextualHelp.showContextualHelp("/index.html")}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "relative block w-full border-t border-gray-300 py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                <div className="flex">
                    <div className="flex-grow">Help</div>
                    <div className="flex-shrink text-right text-gray-500">
                        ?
                    </div>
                </div>
            </button>
        ),
        (active) => (
            <button
                onClick={() =>
                    contextualHelp.showContextualHelp("/shortcuts.html")
                }
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                Shortcut List
            </button>
        ),
        (active) => (
            <button
                onClick={() =>
                    contextualHelp.showContextualHelp("/getting-started.html")
                }
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                Getting Started
            </button>
        ),
        (active) => (
            <button
                onClick={() => {
                    setUpdatesOpen(true);
                    setLocalStorageItem("lastVer", currentVer);
                }}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                <div className="flex items-center space-x-2">
                    <span>Latest Updates</span>
                    <div
                        className={packClasses(
                            "inline-block h-2 w-2 animate-pulse rounded-full bg-green-600",
                            showUpdateNotification ? "" : "hidden"
                        )}
                    >
                        &nbsp;
                    </div>
                </div>
            </button>
        ),
        (active) => (
            <button
                onClick={props.requestLogout}
                className={classNames(
                    active ? "bg-gray-100" : "",
                    "relative block w-full border-t border-gray-300 py-2 px-4 text-left text-xs text-gray-700"
                )}
            >
                Sign out
            </button>
        ),
    ];

    const createMenu = [
        <button
            className="w-full text-left"
            type="button"
            onClick={props.requestShowDropzone}
        >
            Upload
        </button>,
        <button
            onClick={() => history.push("/app/article/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Article</div>
                <div className="text-right text-xs text-gray-500">C then A</div>
            </div>
        </button>,
        <button
            onClick={() => history.push("/app/planner/assignment/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Assignment</div>
                <div className="text-right text-xs text-gray-500">C then N</div>
            </div>
        </button>,
        <button
            onClick={() => history.push("/app/page/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Page</div>
                <div className="text-right text-xs text-gray-500">C then P</div>
            </div>
        </button>,
        <button
            onClick={() => history.push("/app/gallery/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Gallery</div>
                <div className="text-right text-xs text-gray-500">C then G</div>
            </div>
        </button>,
        <button
            onClick={() => history.push("/app/container/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Section/Container</div>
                <div className="text-right text-xs text-gray-500">C then C</div>
            </div>
        </button>,
        <button
            onClick={() => history.push("/app/custom-content/entry/new")}
            className="w-full text-left"
        >
            <div className="flex">
                <div className="flex-grow">Custom Entry</div>
                <div className="text-right text-xs text-gray-500">C then E</div>
            </div>
        </button>,
    ];

    const handleSearch = (e) => {
        if (e.key !== "Enter") {
            return;
        }

        history.push(`/app/search?keyword=${e.target.value}&nonce=-1`);
    };

    const showAboutModal = () => {
        modal({
            title: false,
            children: (
                <div>
                    <div className="flex items-start space-x-4">
                        <div className="min-w-min">
                            <img
                                className="h-8 w-12"
                                src={flatCube}
                                alt="CEO"
                            />
                        </div>
                        <div className="">
                            <div className="text-2xl text-gray-800">
                                CEO by SNworks
                            </div>
                            <div className="text-xs text-gray-500">
                                &copy; 2000 - {new Date().getFullYear()} SN
                                Media Group
                            </div>
                            <div className="mt-4 space-y-4">
                                <p>SNworks is a subsidiary of SN Media Group</p>
                                <p>
                                    Use of this service is subject to the terms
                                    of your customer agreement and SNworks{" "}
                                    <a
                                        href="/static/tos.html"
                                        rel="noopener,nofollow"
                                        target="_blank"
                                        className="text-green-600 underline"
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    and{" "}
                                    <a
                                        href="/static/aup.html"
                                        rel="noopener,nofollow"
                                        target="_blank"
                                        className="text-green-600 underline"
                                    >
                                        Acceptable Use Policy
                                    </a>
                                    .
                                </p>
                                <p>
                                    Made with{" "}
                                    <FontAwesomeIcon
                                        icon={solid("heart")}
                                        className="text-green-600"
                                    />{" "}
                                    by the staff and students at The State News
                                    at Michigan State University in East
                                    Lansing, Michigan.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ),
            buttons: () => (
                <PrimaryButton onClick={modal.dismiss}>Close</PrimaryButton>
            ),
        });
    };

    return (
        <AppContext.Provider
            value={{
                setHomeNav,
                setCurrentNav,
                setEntryNav,
                setSearchNav,
                setPlannerNav,
            }}
        >
            <Fragment>
                <UpdatePanel open={updatesOpen} onClose={setUpdatesOpen} />
                <div className="min-h-full">
                    {!lastNotice && (
                        <div className="space-x-2 bg-yellow-300 py-2 px-4 text-center text-sm text-black">
                            <strong className="font-bold uppercase">
                                Notice:
                            </strong>
                            <div className="inline-block">
                                SNworks is establishing a weekly maintenance
                                window on Wednesdays from 5am to 7am ET,
                                starting March 6. During this time, CEO{" "}
                                <em>may</em> be unavailable.
                            </div>
                            <button
                                type="button"
                                className="rounded border border-yellow-500 bg-yellow-400 px-2 py-1 text-black"
                                onClick={() => {
                                    setLocalStorageItem(
                                        "lastNotice20240110",
                                        true
                                    );
                                    setLastNotice(true);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    )}
                    {/* When the mobile menu is open, add `overflow-hidden` to the `body` element to prevent double scrollbars */}
                    <Popover
                        as="header"
                        className={({ open }) =>
                            classNames(
                                open
                                    ? "fixed inset-0 z-40 overflow-y-auto"
                                    : "",
                                "bg-white shadow-sm lg:static lg:overflow-y-visible"
                            )
                        }
                    >
                        {({ open }) => (
                            <Fragment>
                                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                                    <div className="relative flex justify-between lg:gap-8 xl:grid xl:grid-cols-12">
                                        <div className="flex md:absolute md:inset-y-0 md:left-0 lg:static xl:col-span-2">
                                            <div className="flex flex-shrink-0 items-center">
                                                <Link to="/app/dashboard">
                                                    <img
                                                        className="block h-8 w-auto"
                                                        src={flatCube}
                                                        alt="CEO"
                                                    />
                                                </Link>
                                                <div className="ml-2 hidden text-lg font-light md:block">
                                                    SNworks CEO
                                                </div>
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1 md:px-8 lg:px-0 xl:col-span-6">
                                            <div className="flex items-center px-6 py-4 md:mx-auto md:max-w-3xl lg:mx-0 lg:max-w-none xl:px-0">
                                                <div className="w-full">
                                                    <label
                                                        htmlFor="search"
                                                        className="sr-only"
                                                    >
                                                        Search
                                                    </label>
                                                    <div className="relative">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <SearchIcon
                                                                className="h-5 w-5 text-gray-400"
                                                                aria-hidden="true"
                                                            />
                                                        </div>
                                                        <input
                                                            id="search"
                                                            name="keyword"
                                                            className="focus:outline-none block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 sm:text-sm"
                                                            placeholder="Search CEO"
                                                            type="search"
                                                            ref={searchRef}
                                                            onKeyDown={
                                                                handleSearch
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center md:absolute md:inset-y-0 md:right-0 lg:hidden">
                                            {/* Mobile menu button */}
                                            <Popover.Button className="focus:outline-none -mx-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-green-500">
                                                <span className="sr-only">
                                                    Open menu
                                                </span>
                                                {open ? (
                                                    <XIcon
                                                        className="block h-6 w-6"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <MenuIcon
                                                        className="block h-6 w-6"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </Popover.Button>
                                        </div>
                                        <div className="hidden lg:flex lg:items-center lg:justify-end xl:col-span-4">
                                            {/* Profile dropdown */}
                                            <Menu
                                                as="div"
                                                className="relative ml-5 flex-shrink-0"
                                            >
                                                <div>
                                                    <Menu.Button className="focus:outline-none flex rounded-full bg-white focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
                                                        <span className="sr-only">
                                                            Open user menu
                                                        </span>
                                                        <div className="relative">
                                                            <img
                                                                className="h-8 w-8 rounded-full"
                                                                src={
                                                                    user.image_url
                                                                }
                                                                alt=""
                                                            />
                                                            <div
                                                                className={packClasses(
                                                                    "absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full bg-green-600",
                                                                    showUpdateNotification
                                                                        ? ""
                                                                        : "hidden"
                                                                )}
                                                            >
                                                                &nbsp;
                                                            </div>
                                                        </div>
                                                    </Menu.Button>
                                                </div>
                                                <Transition
                                                    as={Fragment}
                                                    enter="transition ease-out duration-100"
                                                    enterFrom="transform opacity-0 scale-95"
                                                    enterTo="transform opacity-100 scale-100"
                                                    leave="transition ease-in duration-75"
                                                    leaveFrom="transform opacity-100 scale-100"
                                                    leaveTo="transform opacity-0 scale-95"
                                                >
                                                    <Menu.Items className="focus:outline-none absolute right-0 z-30 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                                                        {userNavigation.map(
                                                            (item, index) => (
                                                                <Menu.Item
                                                                    key={`mun-${index}`}
                                                                >
                                                                    {({
                                                                        active,
                                                                    }) =>
                                                                        item(
                                                                            active
                                                                        )
                                                                    }
                                                                </Menu.Item>
                                                            )
                                                        )}
                                                    </Menu.Items>
                                                </Transition>
                                            </Menu>
                                            <div>
                                                <button
                                                    id="help-fixture"
                                                    className="focus:outline-none ml-4 flex cursor-help rounded-full text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                                                    type="button"
                                                    onClick={() =>
                                                        contextualHelp.showContextualHelp(
                                                            "/index.html"
                                                        )
                                                    }
                                                >
                                                    <SupportIcon
                                                        className="h-10 w-10"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="sr-only">
                                                        Help
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Popover.Panel
                                    as="nav"
                                    className="lg:hidden"
                                    aria-label="Global"
                                >
                                    {({ close }) => (
                                        <Fragment>
                                            <div className="mx-auto mb-6 max-w-3xl px-4 sm:px-6">
                                                <Popover.Button
                                                    as={Link}
                                                    to="/app/article/new"
                                                    className="flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700"
                                                >
                                                    New Article
                                                </Popover.Button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        props.requestShowDropzone();
                                                        close();
                                                    }}
                                                    className="mt-4 flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700"
                                                >
                                                    Upload File
                                                </button>
                                            </div>

                                            <div className="mx-auto max-w-3xl space-y-1 px-2 pt-2 pb-3 sm:px-4">
                                                {navigation.map((item) => (
                                                    <Popover.Button
                                                        as={Link}
                                                        key={`g-${item.name}`}
                                                        to={
                                                            item.to
                                                                ? item.to
                                                                : "/app"
                                                        }
                                                        aria-current={
                                                            item.isCurrent(
                                                                currentNav
                                                            )
                                                                ? "page"
                                                                : undefined
                                                        }
                                                        className={classNames(
                                                            item.isCurrent(
                                                                currentNav
                                                            )
                                                                ? "bg-gray-100 text-gray-900"
                                                                : "hover:bg-gray-50",
                                                            item?.className &&
                                                                item.className,
                                                            "block cursor-pointer rounded-md py-2 px-3 text-base font-medium"
                                                        )}
                                                    >
                                                        {item.name}
                                                    </Popover.Button>
                                                ))}
                                            </div>
                                            <div className="border-t border-gray-200 pt-4">
                                                <div className="mx-auto flex max-w-3xl items-center px-4 sm:px-6">
                                                    <div className="flex-shrink-0">
                                                        <img
                                                            className="h-10 w-10 rounded-full"
                                                            src={user.image_url}
                                                            alt=""
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-base font-medium text-gray-800">
                                                            {user.name}
                                                        </div>
                                                        <div className="text-sm font-medium text-gray-500">
                                                            {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mx-auto mt-3 mb-4 max-w-3xl space-y-1 px-2 sm:px-4">
                                                    {userNavigation.map(
                                                        (item, index) => (
                                                            <Popover.Button
                                                                key={`un-${index}`}
                                                                as={`div`}
                                                            >
                                                                {item(false)}
                                                            </Popover.Button>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </Fragment>
                                    )}
                                </Popover.Panel>
                            </Fragment>
                        )}
                    </Popover>
                    <div className="py-10">
                        <div className="mx-auto lg:grid lg:grid-cols-12 lg:gap-8 lg:px-8">
                            <div className="hidden lg:col-span-2 lg:block">
                                <div className="mb-2 flex items-center border-b border-gray-300 pb-2 text-center text-sm text-gray-800">
                                    <NewspaperIcon className="mr-2 h-6 w-6 text-gray-400" />{" "}
                                    {auth.publication.name}
                                    <button
                                        onClick={props.requestPublicationChange}
                                    >
                                        <LogoutIcon className="ml-2 h-4 w-4 text-gray-400" />
                                    </button>
                                </div>
                                <nav
                                    aria-label="Sidebar"
                                    className="sticky top-4 z-10 divide-y divide-gray-300"
                                >
                                    <div className="space-y-1 pb-8">
                                        <SegmentedMenu
                                            label={
                                                <Link
                                                    id="new-article-fixture"
                                                    to="/app/content/new"
                                                    className="inline-block w-full"
                                                >
                                                    New Article
                                                </Link>
                                            }
                                            className="mb-4"
                                            variant="primary"
                                            menuItems={createMenu}
                                        />
                                        {navigation.map((item, i) => (
                                            <Link
                                                key={`un2-${i}`}
                                                to={item.to ? item.to : "/app"}
                                                className={classNames(
                                                    item.isCurrent(currentNav)
                                                        ? "bg-gray-200 text-gray-900"
                                                        : "text-gray-600 hover:bg-gray-50",
                                                    item?.className &&
                                                        item.className,
                                                    "group flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-left text-sm font-medium"
                                                )}
                                                aria-current={
                                                    item.isCurrent(currentNav)
                                                        ? "page"
                                                        : undefined
                                                }
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        item.isCurrent(
                                                            currentNav
                                                        )
                                                            ? "text-gray-500"
                                                            : "text-gray-400 group-hover:text-gray-500",
                                                        "-ml-1 mr-3 h-6 w-6 flex-shrink-0"
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                <span className="truncate">
                                                    {item.name}
                                                </span>
                                            </Link>
                                        ))}
                                        <div className="space-y-0.5 md:ml-11">
                                            {panels.map((panel, index) => (
                                                <div key={`pnl-${index}`}>
                                                    <Link
                                                        className="text-xs text-gray-600"
                                                        to={`/app/custom-content/setting/${panel.uuid}`}
                                                    >
                                                        {panel.title}
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="py-5">
                                        <SecondaryButton
                                            onClick={() => {
                                                window.open(
                                                    "https://snworks.zendesk.com/hc/en-us/requests/new",
                                                    "_blank"
                                                );
                                            }}
                                            className="w-full"
                                            label="HELP!"
                                        />
                                    </div>
                                    <div className="pt-10">
                                        <p
                                            className="text-xs font-semibold uppercase tracking-wider text-gray-500"
                                            id="recent-moves-headline"
                                        >
                                            Recent Items
                                        </p>
                                        <div
                                            className="mt-3 space-y-2"
                                            aria-labelledby="recent-moves-headline"
                                        >
                                            {take(props.recentItems, 5).map(
                                                (item) => (
                                                    <Link
                                                        key={item.uuid}
                                                        to={`/app/${
                                                            item.object_type ===
                                                                "container" &&
                                                            item.type !==
                                                                "gallery"
                                                                ? "container"
                                                                : item.type
                                                        }/${item.uuid}`}
                                                        className="group flex items-center rounded-md py-1 px-1 text-xs  text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                                    >
                                                        <span className="truncate">
                                                            {item.title}
                                                        </span>
                                                    </Link>
                                                )
                                            )}
                                            {props.recentItems.length > 5 && (
                                                <Link
                                                    to={`/app/recent`}
                                                    className="group flex items-center rounded-md py-1 px-1 text-xs  text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                                >
                                                    <span className="truncate">
                                                        More Recent Items
                                                    </span>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="pt-10">
                                        <p
                                            className="text-xs font-semibold uppercase tracking-wider text-gray-500"
                                            id="recent-moves-headline"
                                        >
                                            Super Awesome Debug Info
                                        </p>
                                        <div className="mt-2 space-y-1 text-xs font-normal text-gray-500">
                                            <div className="flex items-center space-x-2">
                                                <span>List Cache:</span>
                                                <span>
                                                    {searchCacheHealth
                                                        ? "Enabled"
                                                        : "Disabled"}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span>Search Index:</span>
                                                <span>{searchIndexHealth}</span>
                                            </div>
                                        </div>
                                    </div>
                                </nav>
                            </div>
                            <UIUpperErrorBoundary>
                                {props.children}
                            </UIUpperErrorBoundary>
                        </div>
                        {settings.settings?.personality && (
                            <div className="mt-4 px-4 text-center sm:hidden sm:px-0 md:block">
                                <p className="text-xs text-gray-600">
                                    <ChatAltIcon className="mr-2 inline h-4 w-4" />{" "}
                                    {arrayRandom(quips)}
                                </p>
                            </div>
                        )}
                        <div className="border-warm-gray-700 mt-4 border-t pt-8 text-center">
                            <p className="font-mono text-xs text-gray-500">
                                ID: {process.env.REACT_APP_APP_ID}
                                &nbsp;&nbsp; VER:{" "}
                                {process.env.REACT_APP_APP_VER}
                            </p>
                            <p className="mt-5 text-xs text-gray-500">
                                Made with{" "}
                                <FontAwesomeIcon icon={regular("heart")} /> in{" "}
                                <FontAwesomeIcon icon={regular("mitten")} />
                                <span className="px-2">&middot;</span>
                                <button type="button" onClick={showAboutModal}>
                                    About
                                </button>
                                <span className="px-2">&middot;</span>
                                <a
                                    href="/static/tos.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Terms
                                </a>
                                <span className="px-2">&middot;</span>
                                <a
                                    href="/static/privacy.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Privacy
                                </a>
                                <span className="px-2">&middot;</span>
                                Copyright {new Date().getFullYear()} SN Media
                                Group
                            </p>
                        </div>
                    </div>
                </div>
                <ChaosMonkey />
            </Fragment>
        </AppContext.Provider>
    );
});
